package com.eladfinish.bible.contest.screens.browse.tableOfContents.ui.dialogs

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.font_size_change
import com.eladfinish.bible.contest.ok
import com.eladfinish.bible.contest.pasuk_example
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.sliders.DottedDiscreteSlider
import com.eladfinish.bible.contest.ui.components.verticalScrollWithScrollbar
import dev.icerock.moko.resources.compose.stringResource
import kotlin.math.roundToInt

private const val TEXT_SIZE = 14f
private const val SEEK_BAR_MAX = 14f
private const val SEEK_BAR_PROGRESS = 7f

private const val MIN_FONT_SIZE_SP = TEXT_SIZE
private const val MAX_FONT_SIZE_SP = TEXT_SIZE + SEEK_BAR_MAX
internal const val DEFAULT_FONT_SIZE_SP = TEXT_SIZE + SEEK_BAR_PROGRESS

@Composable
internal fun FontSizeDialog(
    initialFontSizeSp: Float,
    minFontSizeSp: Float = MIN_FONT_SIZE_SP,
    maxFontSizeSp: Float = MAX_FONT_SIZE_SP,
    title: String = stringResource(SharedRes.strings.font_size_change),
    previewText: String = stringResource(SharedRes.strings.pasuk_example),
    okText: String = stringResource(SharedRes.strings.ok),
    cancelText: String = stringResource(SharedRes.strings.cancel),
    onConfirm: (Float) -> Unit,
    onDismiss: () -> Unit,
) {
    val maxProgress = (maxFontSizeSp - minFontSizeSp).roundToInt().coerceAtLeast(0)

    // Keep a single source of truth: progress (0..maxProgress)
    var progress by rememberSaveable(initialFontSizeSp, minFontSizeSp, maxFontSizeSp) {
        mutableStateOf(
            (initialFontSizeSp - minFontSizeSp)
                .roundToInt()
                .coerceIn(0, maxProgress)
                .toFloat()
        )
    }

    val selectedFontSizeSp = minFontSizeSp + progress

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(text = title) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScrollWithScrollbar()
                    .padding(horizontal = 8.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                DottedDiscreteSlider(
                    progress = progress,
                    onProgressChange = { progress = it },
                    maxProgress = maxProgress,
                )

                Spacer(Modifier.height(12.dp))

                val lineHeightMultiplier = when {
                    selectedFontSizeSp < 18f -> 1.3f
                    selectedFontSizeSp < 26f -> 1.4f
                    else -> 1.5f
                }

                Text(
                    text = previewText,
                    fontSize = selectedFontSizeSp.sp,
                    lineHeight = (selectedFontSizeSp * lineHeightMultiplier).sp,
                    textAlign = TextAlign.Center,
                )
            }
        },
        confirmButton = {
            TextButton(
                onClick = { onConfirm(selectedFontSizeSp) } // ✅ now saves the chosen value
            ) { Text(okText) }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(cancelText) }
        },
    )
}


@Preview(showBackground = true)
@Composable
private fun FontSizeDialogPreview() {
    PreviewTheme {
        FontSizeDialog(
            initialFontSizeSp = DEFAULT_FONT_SIZE_SP,
            minFontSizeSp = MIN_FONT_SIZE_SP,
            maxFontSizeSp = MAX_FONT_SIZE_SP,
            title = "שינוי גודל טקסט",
            previewText = "וַיֹּאמֶר אֱלֹהִים יְהִי אוֹר וַיְהִי אוֹר",
            okText = "אישור",
            cancelText = "ביטול",
            onConfirm = {},
            onDismiss = {},
        )
    }
}
